import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { SectionBox } from '@shared/components/ui/SectionBox';
import type { ReadyToPrepareByHourRow } from '../../models';

interface OrdersByHourChartProps {
  rows: ReadyToPrepareByHourRow[];
}

/** Hora en punto para el eje (0–23 → 00:00 … 23:00). */
function formatRoundHourLabel(hour: number): string {
  const h = Math.max(0, Math.min(23, Math.floor(hour)));
  return `${String(h).padStart(2, '0')}:00`;
}

export const OrdersByHourChart = ({ rows }: OrdersByHourChartProps) => {
  const { t } = useTranslation('readyToPrepareByHourModule');

  const chartData = useMemo(() => {
    const map = new Map<number, number>();
    for (const r of rows) {
      const h = r.hour;
      map.set(h, (map.get(h) ?? 0) + r.order_count);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([hour, total]) => ({
        name: formatRoundHourLabel(hour),
        pedidos: total,
      }));
  }, [rows]);

  if (chartData.length === 0) {
    return null;
  }

  const n = chartData.length;
  /** Rotar antes (p. ej. >8) y con más margen para que las horas no queden pegadas. */
  const angled = n > 8;
  const bottomMargin = angled ? 40 : 28;
  const xHeight = angled ? 64 : 44;

  return (
    <SectionBox title={t('chart.title', 'Total por hora')}>
      <p className="text-xs text-muted-foreground -mt-2 mb-2 max-w-3xl">
        {t('chart.axis_hour_hint', 'Hora en punto (inicio de cada hora).')}
      </p>
      <div className="h-[300px] w-full min-w-0" data-testid="ready-to-prepare-chart">
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: bottomMargin }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              tickMargin={10}
              interval={0}
              angle={angled ? -38 : 0}
              textAnchor={angled ? 'end' : 'middle'}
              height={xHeight}
            />
            <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number | string) => [value, t('chart.orders', 'Pedidos')]}
            />
            <Bar
              dataKey="pedidos"
              fill="hsl(var(--primary))"
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionBox>
  );
};
